import React from "react";

import { diffTokens, tokenize } from "../../sentenceDiff";

// Per character for these, as in play: whitespace-splitting Japanese or Chinese gives one
// token, so the whole sentence would light up as changed.
const SPACELESS_ISOS = ["ja", "zh", "jpn", "cmn"];

const FIELD_LABELS = {
  text: "Sentence",
  translation: "Translation"
};

// Exact comparison on purpose - no case or accent folding, and trailing punctuation
// counts. In play a missing final period is forgivable; in an edit about to be saved it
// is the change.
export function wordDiff(before, after, { spaceless = false } = {}) {
  const beforeTokens = tokenize(before, spaceless);
  const afterTokens = tokenize(after, spaceless);
  const ops = diffTokens(afterTokens, beforeTokens);

  return {
    after: ops
      .filter((o) => o.op !== "extra")
      .map((o) => ({ changed: o.op === "missing", token: afterTokens[o.expectedIndex] })),
    before: ops
      .filter((o) => o.op !== "missing")
      .map((o) => ({ changed: o.op === "extra", token: beforeTokens[o.actualIndex] }))
  };
}

// Translucent backgrounds so the same colours read on the light and dark themes.
const REMOVED_STYLE = { backgroundColor: "rgba(217, 83, 79, 0.25)", textDecoration: "line-through" };
const ADDED_STYLE = { backgroundColor: "rgba(92, 184, 92, 0.3)", textDecoration: "none" };

function renderTokens(tokens, { added, joiner }) {
  return tokens.map((t, i) => {
    let token = t.token;
    if(t.changed) {
      token = added
        ? <ins className="ai-diff-added" style={ADDED_STYLE}>{t.token}</ins>
        : <del className="ai-diff-removed" style={REMOVED_STYLE}>{t.token}</del>;
    }

    return (
      <React.Fragment key={i}>
        {i > 0 && joiner}
        {token}
      </React.Fragment>
    );
  });
}

// The AI's proposed edit as a diff against the sentence as it stands, one block per field
// that actually changes - an unchanged field gets nothing, not a "no change" line. Only
// rendered for a fix the server says is applicable (proposedChanges), which is already
// empty for withheld and stale suggestions.
export default function AiSuggestedChanges(props) {
  const { applyDisabled, applyDisabledReason, changes, current, languageIsos, onApply } = props;
  const fields = ["text", "translation"].filter((f) => changes && Object.prototype.hasOwnProperty.call(changes, f));

  if(!fields.length) {
    return null;
  }

  return (
    <div className="ai-suggested-changes" style={{ marginTop: 5 }}>
      {fields.map((field) => {
        const spaceless = SPACELESS_ISOS.includes((languageIsos || {})[field]);
        const { after, before } = wordDiff(current[field], changes[field], { spaceless });
        const joiner = spaceless ? "" : " ";

        return (
          <div className={"ai-suggested-change ai-suggested-change-" + field} key={field} style={{ marginBottom: 5 }}>
            <small><strong>{FIELD_LABELS[field]}:</strong></small>
            <div className="ai-diff-before">
              <small>
                <span className="text-muted">&minus; </span>
                {renderTokens(before, { added: false, joiner })}
              </small>
            </div>
            <div className="ai-diff-after">
              <small>
                <span className="text-muted">+ </span>
                {renderTokens(after, { added: true, joiner })}
              </small>
            </div>
          </div>
        );
      })}
      {!!onApply && (
        <div>
          <button
            className="btn btn-success btn-xs"
            disabled={!!applyDisabled}
            onClick={onApply}
            title={applyDisabledReason || "Apply these changes and resolve the report"}
          >
            Apply changes
          </button>
          {!!applyDisabledReason && <div><small className="text-muted">{applyDisabledReason}</small></div>}
        </div>
      )}
    </div>
  );
}
